/**
 * Boundary Bounds Calculator
 *
 * Utilities for calculating bounding boxes from encoded boundary data.
 * Supports both single boundaries and MultiPolygon structures.
 */

import { decodePolylineToCoords } from '../utils/polylineUtils';

/**
 * Calculate bounds from encoded boundary (without Google Maps API)
 * Returns plain object that can be used for calculations
 *
 * @param encodedBoundary - Single encoded polyline string OR MultiPolygon array structure
 * @returns Bounding box with min/max lat/lng, or null if no valid bounds found
 *
 * @example
 * // Single boundary
 * const bounds = calculateBoundsFromEncodedBoundary('u`jeFnzbjVu@cA}@aB');
 * // => { minLat: 37.86, maxLat: 37.87, minLng: -122.27, maxLng: -122.26 }
 *
 * @example
 * // MultiPolygon (array of polygons, each with outer ring + optional holes)
 * const bounds = calculateBoundsFromEncodedBoundary([
 *   ['outer_ring_1', 'hole_1', 'hole_2'],
 *   ['outer_ring_2']
 * ]);
 */
export function calculateBoundsFromEncodedBoundary(
  encodedBoundary: string | Array<Array<string>>,
): { minLat: number; maxLat: number; minLng: number; maxLng: number } | null {
  // Normalize input to array format
  const boundaries: Array<Array<string>> = typeof encodedBoundary === 'string' ? [[encodedBoundary]] : encodedBoundary;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  // Decode all polygons and find min/max bounds
  for (const polygonRings of boundaries) {
    for (const encodedRing of polygonRings) {
      const points = decodePolylineToCoords(encodedRing);

      for (const point of points) {
        minLat = Math.min(minLat, point.lat);
        maxLat = Math.max(maxLat, point.lat);
        minLng = Math.min(minLng, point.lng);
        maxLng = Math.max(maxLng, point.lng);
      }
    }
  }

  // Check if we found valid bounds
  if (!isFinite(minLat) || !isFinite(maxLat) || !isFinite(minLng) || !isFinite(maxLng)) {
    return null;
  }

  return { minLat, maxLat, minLng, maxLng };
}
